// @flow
import React, {useState, useCallback, useEffect, useRef} from 'react';
import {View} from 'react-native';
import config from '../../../config';
import I18n from '../../../lib/i18n';
import PlanSliders from '../PlanSliders';
import RoamingPlanSliders from './RoamingPlanSliders.container';
import Stepper from './Stepper';

type Props = {
  hasRoamingOptions: boolean,
  localDataOption?: Object,
  roamingDataOption?: Object,
  removeOptionFromNewOffer: Function,
  withZoneValidity?: boolean,
  orderType: string,
  isChangingPlanDuringTurnBackOn?: boolean,
  subscriptionToUpdateOuid?: string,
  isUniqueOffer?: boolean,
  currentStep?: number,
  onStepChange?: (step: number) => void,
};

const StepperPlanSliders = (props: Props) => {
  const [internalStep, setInternalStep] = useState(0);
  const currentStep = props.currentStep !== undefined ? props.currentStep : internalStep;

  // Track whether PlanSliders has mounted at least once in this screen session.
  // On first mount: isInitialMount=true → force 150Go default for DATA.
  // On step-switch remount: isInitialMount=false → preserve user's selection.
  const hasInitializedRef = useRef(false);

  const isLocalDataZero =
    !props.localDataOption?.ouid ||
    props.localDataOption?.characteristic?.value === '0';

  useEffect(() => {
    if (isLocalDataZero && props.roamingDataOption?.ouid) {
      props.removeOptionFromNewOffer(props.roamingDataOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalDataZero]);

  // Compute isInitialMount BEFORE rendering PlanSliders, then mark the ref.
  // This ensures the first PlanSliders mount sees true, subsequent remounts see false.
  const isInitialMount = !hasInitializedRef.current;
  useEffect(() => {
    hasInitializedRef.current = true;
  }, []);

  const handleStepPress = useCallback(
    (index: number) => {
      setInternalStep(index);
      if (props.onStepChange) {
        props.onStepChange(index);
      }
    },
    [props.onStepChange],
  );

  // No roaming options OR feature flag off OR unique offer → render PlanSliders directly
  if (
    !config.displayRoamingPlanSliders ||
    !props.hasRoamingOptions ||
    props.isUniqueOffer
  ) {
    return (
      <PlanSliders
        withZoneValidity={props.withZoneValidity}
        subscriptionToUpdateOuid={props.subscriptionToUpdateOuid}
        isChangingPlanDuringTurnBackOn={props.isChangingPlanDuringTurnBackOn}
        orderType={props.orderType}
        isUniqueOffer={props.isUniqueOffer}
        isInitialMount={isInitialMount}
      />
    );
  }

  const steps = [
    {label: I18n.t('stepper.step1Label')},
    {label: I18n.t('stepper.step2Label')},
  ];

  return (
    <View>
      <Stepper
        currentStep={currentStep}
        steps={steps}
        onStepPress={handleStepPress}
      />
      {currentStep === 0 && (
        <PlanSliders
          withZoneValidity={props.withZoneValidity}
          subscriptionToUpdateOuid={props.subscriptionToUpdateOuid}
          isChangingPlanDuringTurnBackOn={props.isChangingPlanDuringTurnBackOn}
          orderType={props.orderType}
          isUniqueOffer={false}
          isInitialMount={isInitialMount}
        />
      )}
      {currentStep === 1 && (
        <RoamingPlanSliders
          isLocalDataZero={isLocalDataZero}
          orderType={props.orderType}
        />
      )}
    </View>
  );
};

export default StepperPlanSliders;
